import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import SEO from "../SEO/SEO";
import registry from "../../seo/contentRegistry.generated.json";
import "./public-content-page.scss";

const { SITE_URL, pages, guides, destinations } = registry;

function slugify(value = "") {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function destinationPage(slug, destination, indexable = true, locationType = "local") {
  return {
    path: locationType === "regional" ? `/esim/regions/${slug}` : `/esim/${slug}`,
    type: "destination",
    indexable,
    title: `${destination.name} eSIM Plans for Travel | Teleport Now`,
    description: `Explore available travel eSIM plans for ${destination.name}. Compare current data allowances, validity and prices using Teleport's live plan search.`,
    eyebrow: "Destination eSIM",
    heading: `${destination.name} eSIM plans for travel`,
    intro: `Use Teleport's live search to review the eSIM plans currently available for ${destination.name}. Plan details and prices are loaded from Teleport's current catalogue.`,
    ctaLabel: "View current eSIM plans",
    ctaPath: `/esim?locationType=${locationType}&location=${destination.code}`,
    destination,
    sections: [
      {
        heading: "What to compare before choosing a plan",
        body: "Check the displayed data allowance, validity period, coverage information and price. Choose only after confirming that your phone supports eSIM and is eligible to use another provider.",
      },
      {
        heading: "Before you travel",
        body: "Review the installation instructions supplied with your plan and keep access to a reliable internet connection during setup. Activation requirements can vary by device and provider.",
      },
    ],
    faqs: [
      {
        question: `Does Teleport show current eSIM plans for ${destination.name}?`,
        answer: `Yes. Use the live plan search to see the plans currently returned by Teleport's catalogue for ${destination.name}. Availability and prices can change.`,
      },
      {
        question: "Does every phone support eSIM?",
        answer: "No. Support varies by phone model, region and carrier status. Check the device manufacturer's guidance and confirm that the phone is eligible before purchasing.",
      },
    ],
  };
}

function breadcrumbsFor(page) {
  const crumbs = [{ label: "Home", path: "/" }];
  if (page.type === "destination") crumbs.push({ label: "eSIM", path: "/esim" });
  if (page.type === "article") crumbs.push({ label: "Guides", path: "/guides" });
  crumbs.push({ label: page.heading, path: page.path });
  return crumbs;
}

function schemaFor(page) {
  const crumbs = breadcrumbsFor(page);
  const graph = [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Teleport Now",
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/logo512.png`,
      sameAs: [registry.APPLE_STORE_URL, registry.ANDROID_STORE_URL],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: "Teleport Now",
      url: `${SITE_URL}/`,
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
    {
      "@type": page.type === "article" ? "Article" : "WebPage",
      "@id": `${SITE_URL}${page.path}#webpage`,
      name: page.title,
      headline: page.type === "article" ? page.heading : undefined,
      description: page.description,
      url: `${SITE_URL}${page.path}`,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      publisher: { "@id": `${SITE_URL}/#organization` },
      dateModified: page.reviewedOn,
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.label,
        item: `${SITE_URL}${crumb.path === "/" ? "/" : crumb.path}`,
      })),
    },
  ];

  if (page.type === "service" || page.type === "destination") {
    graph.push({
      "@type": "Service",
      "@id": `${SITE_URL}${page.path}#service`,
      name: page.heading,
      description: page.description,
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: page.destination?.name,
    });
  }

  if (page.faqs?.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: page.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

export function PublicContentPage({ page }) {
  const crumbs = breadcrumbsFor(page);
  const guideList = Object.entries(guides);
  const destinationList = Object.entries(destinations);

  return (
    <>
      <SEO
        title={page.title}
        description={page.description}
        canonicalPath={page.path}
        robots={page.indexable ? "index, follow, max-image-preview:large" : "noindex, follow"}
        jsonLd={schemaFor(page)}
      />
      <Navbar />
      <main className="public-content-page">
        <nav className="public-breadcrumbs" aria-label="Breadcrumb">
          <ol>
            {crumbs.map((crumb, index) => (
              <li key={crumb.path}>
                {index < crumbs.length - 1 ? <Link to={crumb.path}>{crumb.label}</Link> : <span aria-current="page">{crumb.label}</span>}
              </li>
            ))}
          </ol>
        </nav>

        <header className="public-content-hero">
          <p className="public-eyebrow">{page.eyebrow || "Teleport Now"}</p>
          <h1>{page.heading}</h1>
          <p>{page.intro}</p>
          {page.ctaPath && <Link className="public-primary-action" to={page.ctaPath}>{page.ctaLabel}</Link>}
        </header>

        {page.type === "collection" && (
          <section className="public-card-grid" aria-labelledby="guide-list-title">
            <h2 id="guide-list-title">Browse travel guides</h2>
            <div>
              {guideList.map(([slug, guide]) => (
                <article key={slug}>
                  <h3><Link to={guide.path}>{guide.heading}</Link></h3>
                  <p>{guide.description}</p>
                  <Link to={guide.path}>Read guide</Link>
                </article>
              ))}
            </div>
          </section>
        )}

        {page.sections?.length > 0 && (
          <section className="public-copy-grid" aria-label={`${page.heading} information`}>
            {page.sections.map((section) => (
              <article key={section.heading}>
                <h2>{section.heading}</h2>
                <p>{section.body}</p>
              </article>
            ))}
          </section>
        )}

        {page.faqs?.length > 0 && (
          <section className="public-faq" aria-labelledby="public-faq-title">
            <h2 id="public-faq-title">Frequently asked questions</h2>
            {page.faqs.map((faq) => (
              <details key={faq.question}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </section>
        )}

        {page.sources?.length > 0 && (
          <aside className="public-sources" aria-labelledby="source-title">
            <h2 id="source-title">Official sources</h2>
            <p>Reviewed {page.reviewedOn}.</p>
            <ul>
              {page.sources.map((source) => (
                <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.label}</a></li>
              ))}
            </ul>
          </aside>
        )}

        {page.type === "destination" && (
          <section className="public-related" aria-labelledby="related-destinations-title">
            <h2 id="related-destinations-title">Other reviewed eSIM destinations</h2>
            <ul>
              {destinationList.filter(([slug]) => page.path !== `/esim/${slug}`).slice(0, 8).map(([slug, destination]) => (
                <li key={slug}><Link to={`/esim/${slug}`}>{destination.name} eSIM</Link></li>
              ))}
            </ul>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

export function RegisteredPublicPage({ pageKey }) {
  return <PublicContentPage page={pages[pageKey]} />;
}

export function GuidePage() {
  const { guideSlug } = useParams();
  const page = pages[`guide:${guideSlug}`];
  return page ? <PublicContentPage page={page} /> : <NotFoundPage />;
}

export function DestinationPage({ destinationKey: explicitKey, regional = false }) {
  const params = useParams();
  const destinationKey = explicitKey || params.destinationSlug;
  const knownPage = pages[`destination:${destinationKey}`];
  const [remotePage, setRemotePage] = useState(null);
  const [checked, setChecked] = useState(Boolean(knownPage));

  useEffect(() => {
    if (knownPage) return;
    let active = true;
    fetch("https://api.teleportapp.sg/esim/getSupportedLocations", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Teleport-Client": "teleport-web" },
      body: "{}",
    })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("Location request failed")))
      .then((payload) => {
        const locations = payload?.obj?.locationList || [];
        const expectedType = regional ? 2 : 1;
        const match = locations.find((location) => location.type === expectedType && slugify(location.name) === destinationKey);
        if (active && match) setRemotePage(destinationPage(destinationKey, { name: match.name, code: match.code }, false, regional ? "regional" : "local"));
      })
      .catch(() => {})
      .finally(() => { if (active) setChecked(true); });
    return () => { active = false; };
  }, [destinationKey, knownPage, regional]);

  const page = useMemo(() => knownPage || remotePage, [knownPage, remotePage]);
  if (page) return <PublicContentPage page={page} />;
  if (!checked) return <LoadingPublicPage />;
  return <NotFoundPage />;
}

function LoadingPublicPage() {
  return (
    <>
      <SEO title="Loading destination | Teleport Now" description="Loading current destination information." canonicalPath="/esim" robots="noindex, follow" />
      <Navbar />
      <main className="public-content-page"><p className="public-status" role="status">Loading destination information…</p></main>
      <Footer />
    </>
  );
}

export function NotFoundPage() {
  return (
    <>
      <SEO title="Page not found | Teleport Now" description="The requested Teleport page could not be found." canonicalPath="/404" robots="noindex, nofollow" />
      <Navbar />
      <main className="public-content-page public-not-found">
        <p className="public-eyebrow">404</p>
        <h1>Page not found</h1>
        <p>The address may be incorrect or the page may have moved.</p>
        <Link className="public-primary-action" to="/">Return home</Link>
      </main>
      <Footer />
    </>
  );
}
